// DCA-A3 · Fully-adjustable forecast parameter panel (Decline Analyzer).
// DCA-A6 · Robust + draggable decline-start.
//
// Spec: docs/domain/review_decline_analyzer_plan.md (DCA-A3/A6, §3A.1 row
//       "ForecastParamsPanel → 改可编辑控件") + docs/domain/dca_tier1_spec.md
//       (§1.4 Dmin default 6%/yr, §4 robust decline start).
//
// Replaces the old read-only EUR table with editable qi / Di / b / Dmin /
// decline-start controls. Any edit re-runs the DCA engine (getDcaForecast)
// against the active well and writes the user-tuned params + EUR back into the
// project slice, so "Save" in ForecastChart persists them (sharing the existing
// save mechanism — no new persistence path). PE acceptance: the re-forecast EUR
// shown here is produced by the same engine endpoint, so manual values match
// the engine.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  selectActiveWell,
  selectDeclinePhase,
  selectDcaCommonParams,
  selectDcaUserParams,
  selectDcaRecommParams,
  selectRecommEurDataAllPhase,
  selectUserEurDataAllPhase,
  setDcaUserParams,
  setDeclineStart,
  setUserEurDataByPhase,
} from "features/decline_analysis/dcaProjectSlice";
import { getDcaForecast, getRecommData } from "service/dcaAnalysis";
import { parseFloatValue } from "utils/dataUtil";
import { pickRobustDeclineStart, snapToHistory } from "utils/declineStart";
import { DECLINE_PHASE_OPTIONS } from "app/codes";

const PreferredDecimal = 2;

// Phase → engineering units for qi (rate). Di/Dmin are %/yr; b is dimensionless.
const QI_UNIT = { oil: "bbl/d", gas: "Mscf/d", liquid: "bbl/d" };

function fmt(value) {
  return value ? Number(value).toFixed(PreferredDecimal) : 0;
}

function isoToInput(iso) {
  // <input type="date"> wants YYYY-MM-DD.
  return iso ? String(iso).slice(0, 10) : "";
}

function ForecastParamsPanel(props) {
  const dispatch = useDispatch();

  const activeWell = useSelector(selectActiveWell);
  const declinePhase = useSelector(selectDeclinePhase);
  const dcaCommonParams = useSelector(selectDcaCommonParams);
  const dcaUserParams = useSelector(selectDcaUserParams);
  const dcaRecommParams = useSelector(selectDcaRecommParams);
  const eurRecommDataAllPhase = useSelector(selectRecommEurDataAllPhase);
  const eurUserDataAllPhase = useSelector(selectUserEurDataAllPhase);

  // Local, immediately-editable mirror of the tunable params. Seeded from the
  // user-tuned slice, falling back to the engine's recommended fit.
  const [qi, setQi] = useState("");
  const [di, setDi] = useState("");
  const [b, setB] = useState("");
  const [dMin, setDMin] = useState(6);
  const [startDate, setStartDate] = useState("");

  // Monthly history of the active well/phase — drives the drag bounds and the
  // robust auto-anchor (DCA-A6). Fetched from the same init endpoint the
  // validation chart uses.
  const [history, setHistory] = useState([]);

  const model = dcaUserParams.declineModel || dcaRecommParams.declineModel;

  // Seed local controls when the well / phase / fitted params change.
  useEffect(() => {
    const src =
      dcaUserParams.declineRate && parseFloatValue(dcaUserParams.declineRate)
        ? dcaUserParams
        : dcaRecommParams;
    setQi(src.declineRate ?? "");
    setDi(src.ipRatio ?? "");
    setB(src.b ?? "");
    setDMin(src.dMin ?? 6);
    setStartDate(isoToInput(dcaCommonParams.startDate));
  }, [
    declinePhase,
    activeWell,
    dcaUserParams,
    dcaRecommParams,
    dcaCommonParams.startDate,
  ]);

  // Load history for the drag track + robust anchor.
  useEffect(() => {
    if (!activeWell?.uwi || !declinePhase) return;
    getRecommData(
      {
        wellId: activeWell.uwi,
        declinePhase,
        validationMonths: dcaCommonParams.validationMonths,
        startDate: dcaCommonParams.startDate,
      },
      (result) => setHistory(result?.history || []),
      () => setHistory([])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWell, declinePhase]);

  const computeUserEur = useCallback((result) => {
    const hist = result?.history;
    const forecast = result?.dca?.predicted;
    if (!hist || !forecast) return null;
    const forecastSum = forecast.reduce((t, item) => t + item[1], 0);
    const historySum = hist.reduce((t, item) => t + item[1], 0);
    return { eurUser: forecastSum + historySum, periodPrdUser: forecastSum };
  }, []);

  // Re-run the engine with the current local params and write user EUR back.
  // This is the single re-forecast path shared by every control (slider/input).
  const reforecast = useCallback(
    (overrides = {}) => {
      if (!activeWell?.uwi || !model) return;
      const params = {
        wellId: activeWell.uwi,
        declinePhase,
        declineModel: model,
        startDate: overrides.startDate ?? dcaCommonParams.startDate,
        forecastMonths: dcaCommonParams.forecastMonths || 240,
        declineRate: overrides.qi ?? qi,
        ipRatio: overrides.di ?? di,
        b: overrides.b ?? b,
        dMin: overrides.dMin ?? dMin,
      };
      if (model === "HYP" && (!params.b || parseFloat(params.b) === 0)) return;

      getDcaForecast(
        params,
        (result) => {
          const userEur = computeUserEur(result);
          if (userEur) {
            dispatch(
              setUserEurDataByPhase({ phase: declinePhase, eurData: userEur })
            );
          }
        },
        (error) => console.error("params-panel reforecast error", error)
      );
    },
    [
      activeWell,
      model,
      declinePhase,
      dcaCommonParams.startDate,
      dcaCommonParams.forecastMonths,
      qi,
      di,
      b,
      dMin,
      dispatch,
      computeUserEur,
    ]
  );

  // Persist current local params into the user-tuned slice. ForecastChart's
  // "Save" button reads eur_user_dca + user params from the slice, so this is
  // the existing persistence path (no duplicate save logic).
  const writeUserParams = useCallback(
    (overrides = {}) => {
      dispatch(
        setDcaUserParams({
          declineModel: model,
          declineRate: overrides.qi ?? qi,
          ipRatio: overrides.di ?? di,
          b: overrides.b ?? b,
          dMin: overrides.dMin ?? dMin,
        })
      );
    },
    [dispatch, model, qi, di, b, dMin]
  );

  // Generic numeric-field commit: update local state, persist, re-forecast.
  const commitField = useCallback(
    (key, raw) => {
      const value = raw;
      const overrides = { [key]: value };
      if (key === "qi") setQi(value);
      if (key === "di") setDi(value);
      if (key === "b") setB(value);
      if (key === "dMin") setDMin(value);
      writeUserParams(overrides);
      reforecast(overrides);
    },
    [writeUserParams, reforecast]
  );

  // --- DCA-A6 decline-start interactions ---------------------------------
  const startBounds = useMemo(() => {
    if (!history.length) return { min: "", max: "", count: 0 };
    return {
      min: isoToInput(history[0][0]),
      max: isoToInput(history[history.length - 1][0]),
      count: history.length,
    };
  }, [history]);

  // Index of the current anchor inside the history series (for the drag slider).
  const startIndex = useMemo(() => {
    if (!history.length || !startDate) return 0;
    return Math.max(0, snapToHistory(history, startDate).index);
  }, [history, startDate]);

  const applyDeclineStart = useCallback(
    (iso, source) => {
      const snapped = snapToHistory(history, iso);
      const date = snapped.date ? isoToInput(snapped.date) : isoToInput(iso);
      setStartDate(date);
      dispatch(setDeclineStart({ startDate: date, source }));
      reforecast({ startDate: date });
    },
    [history, dispatch, reforecast]
  );

  // Drag handler over the history track (range slider = index into history).
  const handleStartDrag = useCallback(
    (e) => {
      const idx = parseInt(e.target.value, 10);
      if (!history[idx]) return;
      applyDeclineStart(history[idx][0], "user");
    },
    [history, applyDeclineStart]
  );

  // Manual date input.
  const handleStartInput = useCallback(
    (e) => {
      const v = e.target.value;
      setStartDate(v);
      if (v) applyDeclineStart(v, "user");
    },
    [applyDeclineStart]
  );

  // Robust auto re-pick: ignore ramp + smoothed peak (DCA-A6 §4.2).
  const handleRobustReset = useCallback(() => {
    const picked = pickRobustDeclineStart(history);
    if (picked.date) applyDeclineStart(picked.date, picked.source);
  }, [history, applyDeclineStart]);

  const startSourceLabel = dcaCommonParams.declineStartSource || "auto_smoothed";

  return (
    // UX-2: docked right-hand sidebar (was a modal popup). Always visible — no
    // open/close entry point. All controls (editable qi/Di/b/Dmin + immediate
    // re-forecast + draggable decline-start + EUR read-out) are unchanged; only
    // the container/position/style moved.
    <div
      className="dca-params-sidebar cards-container d-flex flex-column h-100"
      style={{ overflowY: "auto" }}
    >
      <div className="cards-header d-flex justify-content-between align-items-center">
        <span className="header-title">Forecast Parameters</span>
      </div>
      <div className="cards-content d-flex flex-column" style={{ overflowY: "auto" }}>

      {/* DCA-A3 editable params */}
      <div className="eur-table">
        <table>
          <tbody>
            <tr>
              <td className="cell-header">Model</td>
              <td colSpan="2">{model || "—"}</td>
            </tr>
            <tr>
              <td className="cell-header">qi ({QI_UNIT[declinePhase]})</td>
              <td colSpan="2">
                <input
                  type="number"
                  value={qi}
                  onChange={(e) => setQi(e.target.value)}
                  onBlur={(e) => commitField("qi", e.target.value)}
                  style={{ width: "100%" }}
                />
              </td>
            </tr>
            <tr>
              <td className="cell-header">Di (%/yr)</td>
              <td colSpan="2">
                <input
                  type="number"
                  step="0.001"
                  value={di}
                  onChange={(e) => setDi(e.target.value)}
                  onBlur={(e) => commitField("di", e.target.value)}
                  style={{ width: "100%" }}
                />
              </td>
            </tr>
            <tr>
              <td className="cell-header">b</td>
              <td colSpan="2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="2"
                  disabled={model === "EXP"}
                  value={b}
                  onChange={(e) => setB(e.target.value)}
                  onBlur={(e) => commitField("b", e.target.value)}
                  style={{ width: "100%" }}
                />
              </td>
            </tr>
            {/* DCA-A1/A3 terminal decline */}
            <tr>
              <td className="cell-header">Dmin (eff. %/yr)</td>
              <td colSpan="2">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="20"
                  value={dMin}
                  onChange={(e) => setDMin(e.target.value)}
                  onBlur={(e) => commitField("dMin", e.target.value)}
                  style={{ width: "100%" }}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* DCA-A6 decline start */}
      <div className="mt-2">
        <div className="d-flex justify-content-between align-items-center">
          <span className="cell-header">Decline Start</span>
          <small style={{ opacity: 0.7 }}>{startSourceLabel}</small>
        </div>
        <div className="d-flex align-items-center mt-1">
          <input
            type="date"
            value={startDate}
            min={startBounds.min}
            max={startBounds.max}
            onChange={handleStartInput}
            style={{ flex: 1 }}
          />
          <button
            className="btn-custom ms-2"
            title="Robust auto pick (skip ramp + smoothed peak)"
            onClick={handleRobustReset}
            disabled={!history.length}
          >
            Auto
          </button>
        </div>
        {startBounds.count > 1 && (
          <input
            type="range"
            className="mt-1"
            min={0}
            max={startBounds.count - 1}
            value={startIndex}
            onChange={handleStartDrag}
            style={{ width: "100%" }}
          />
        )}
      </div>

      {/* EUR readout (engine output) — kept so manual edits can be checked
          against the engine result (PE acceptance). */}
      <div className="eur-table mt-2">
        <table>
          <thead>
            <tr>
              <td rowSpan="2" className="cell-header">
                Phase
              </td>
              <th colSpan="2" className="cell-header">
                EUR
              </th>
              <th colSpan="2" className="cell-header">
                Period Production
              </th>
            </tr>
            <tr>
              <th className="cell-header">Auto</th>
              <th className="cell-header">User</th>
              <th className="cell-header">Auto</th>
              <th className="cell-header">User</th>
            </tr>
          </thead>
          <tbody>
            {DECLINE_PHASE_OPTIONS.map((option, index) => (
              <tr key={index}>
                <th className="cell-header">
                  {option.name + "(" + option.unit.slice(0, -7) + ")"}
                </th>
                <td>{fmt(eurRecommDataAllPhase[option.value].eurRecomm)}</td>
                <td>{fmt(eurUserDataAllPhase[option.value].eurUser)}</td>
                <td>
                  {fmt(eurRecommDataAllPhase[option.value].periodPrdRecomm)}
                </td>
                <td>{fmt(eurUserDataAllPhase[option.value].periodPrdUser)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

export default ForecastParamsPanel;
